import { useState, useCallback } from 'react'
import { useGameSelection } from './useGameSelection'
import { useGameDisambiguation } from './useGameDisambiguation'
import { UserLibraryService } from './userLibraryService'
import { GameProcessingService } from './gameProcessingService'
import { Game } from '@/types/database'
import {
  AppUser,
  SteamGame,
  GameManagementReturn,
  GameOption
} from './types'

// Helper function to convert GameOption to Game interface
function convertGameOptionToGame(option: GameOption): Game {
  return {
    id: option.id,
    title: option.title,
    provider: option.provider || 'igdb',
    igdb_id: option.id,
    background_image: option.background_image,
    release_date: option.release_date
  }
}

export const useGameManagement = (
  user: AppUser | null,
  steamGames: SteamGame[],
  selectedGames: Set<string>,
  setSelectedGames: (games: Set<string>) => void,
  setError: (error: string) => void
): GameManagementReturn & {
  userLibraryGames: Map<number, string[]>
  disambiguationOpen: boolean
  disambiguationGames: GameOption[]
  fetchUserLibrary: () => Promise<void>
  onSelectGame: (game: GameOption) => void
  onCancelDisambiguation: () => void
  toggleGameSelection: (gameId: string) => void
} => {
  const [userLibraryGames, setUserLibraryGames] = useState<
    Map<number, string[]>
  >(new Map())
  const {
    isOpen: disambiguationOpen,
    games: disambiguationGames,
    showDisambiguation,
    onSelectGame,
    onCancel,
  } = useGameDisambiguation()
  const { toggleGameSelection } = useGameSelection(
    selectedGames,
    setSelectedGames
  )

  const addSelectedGamesToLibrary = async () => {
    if (!user?.id) {
      console.error('No user ID found')
      return
    }

    try {
      // Step 1: Process selected games and handle conflicts
      const gameProcessingResults = await GameProcessingService.processSelectedGames(
        steamGames,
        selectedGames
      )

      // Step 2: Separate conflicts from resolved games
      const conflicts = gameProcessingResults.filter(result => result?.type === 'conflict')
      const resolvedGames = gameProcessingResults.filter(result => result?.type !== 'conflict' && result !== null)

      // Step 3: Handle conflicts sequentially
      for (const conflict of conflicts) {
        if (conflict.possibleMatches) {
          const userChoice = await showDisambiguation(conflict.possibleMatches)
          if (userChoice) {
            const selectedIgdbGame = conflict.possibleMatches.find(g => g.id === userChoice.id)
            if (selectedIgdbGame) {
              resolvedGames.push({
                steamGame: conflict.steamGame,
                igdbData: convertGameOptionToGame(selectedIgdbGame),
                genres: conflict.genres,
                existingIgdbGame: true,
              })
            }
          }
        }
      }

      // Step 4: Insert games and process genres
      const insertedGames = await GameProcessingService.insertGamesAndProcessGenres(resolvedGames)

      // Step 5: Link games to user
      await UserLibraryService.linkGamesToUser(user.id, insertedGames)

      // Step 6: Refresh library and show success
      await fetchUserLibrary()
      showSuccessToast()
      setSelectedGames(new Set<string>())
    } catch (error) {
      console.error('Error adding games to library:', error)
      showErrorToast()
    }
  }

  const showSuccessToast = () => {
    const addToast = document.getElementById('success-toast')
    if (addToast) addToast.classList.remove('hidden')
    setTimeout(() => {
      const addToast = document.getElementById('success-toast')
      if (addToast) addToast.classList.add('hidden')
    }, 3000)
  }

  const showErrorToast = () => {
    const errorToast = document.getElementById('error-toast')
    if (errorToast) errorToast.classList.remove('hidden')
    setTimeout(() => {
      const errorToast = document.getElementById('error-toast')
      if (errorToast) errorToast.classList.add('hidden')
    }, 3000)
  }
  const fetchUserLibrary = useCallback(async () => {
    if (!user?.id) {
      console.error('No user ID found for fetching library')
      return
    }

    try {
      const gameMap = await UserLibraryService.getUserLibrary(user.id, steamGames)
      setUserLibraryGames(gameMap)
    } catch (error) {
      console.error('Error fetching user library:', error)
      setError('Failed to fetch user library')
    }
  }, [user?.id, steamGames, setError])

  return {
    userLibraryGames,
    disambiguationOpen,
    disambiguationGames,
    handleProcessSelectedGames: addSelectedGamesToLibrary,
    processingGames: false, // TODO: Add processing state
    gameProcessingResults: [], // TODO: Add results state
    resolvedGameChoices: new Map(), // TODO: Add choices state
    setResolvedGameChoices: () => {}, // TODO: Add setter
    handleResolveConflicts: async () => {}, // TODO: Add conflict resolution
    resolvingConflicts: false, // TODO: Add resolving state
    fetchUserLibrary,
    onSelectGame,
    onCancelDisambiguation: onCancel,
    toggleGameSelection,
  }
}
